import { useMutation, useQueryClient } from 'react-query';
import isArray from 'lodash/isArray';

import { CreateItemGqlQuery, CreateItemCacheKeys } from '../../../../../types';

import { createCacheItem } from '../../actions/createCacheItem';

import {
  ErrorType,
  parseRequestError
} from '../../../../../utils/parseRequestError';

interface CacheCreateQueryOptions<CreateQueryInput, ItemType> {
  action: string;
  query: CreateItemGqlQuery;
  cacheKeys?: CreateItemCacheKeys;
  onOptimisticCreate?: (input: CreateQueryInput) => null | (() => void);
  onSuccess?: (responseRecord: ItemType) => void;
  refetchInactive?: boolean;
}

interface CacheCreateQueryContext {
  rollback?: () => void;
}

function useCacheCreateQuery<
  CreateQueryInput,
  CreateQueryResponse,
  CreateQueryError extends ErrorType,
  ItemType
>({
  action,
  query,
  cacheKeys,
  onOptimisticCreate,
  onSuccess,
  refetchInactive = false
}: CacheCreateQueryOptions<CreateQueryInput, ItemType>) {
  const queryClient = useQueryClient();

  const { data, error, isLoading, mutateAsync, reset } = useMutation<
    CreateQueryResponse,
    CreateQueryError,
    CreateQueryInput,
    CacheCreateQueryContext
  >(
    (queryInput) =>
      createCacheItem<CreateQueryInput, CreateQueryResponse>({
        action,
        query,
        queryInput
      }),
    {
      onMutate: (input) => {
        const rollback = onOptimisticCreate?.(input) || undefined;

        return { rollback };
      },
      onError: (error, variables, context) => {
        context?.rollback?.();
      },
      onSuccess: (response) => {
        onSuccess?.(response?.[action]?.record);
      },
      onSettled: async (response) => {
        if (isArray(cacheKeys)) {
          cacheKeys.map((eachCacheKey) =>
            queryClient.invalidateQueries(eachCacheKey, { refetchInactive })
          );
        }
      }
    }
  );

  return {
    createCacheQueryData: data,
    createCacheQueryError: error,
    createCacheQueryLoading: isLoading,
    createCacheQueryErrorMessage: parseRequestError(error),
    createCacheQuery: mutateAsync,
    createCacheQueryReset: reset
  };
}

export default useCacheCreateQuery;
